/**
 * Engagement highlight rules matching zReports unsigned email tables
 * (get_highlighting_configurations highlight_range_config).
 * Yellow wins over green when both would match.
 *
 * Rate columns use percentage points (0–100). Stored API ratios without `%`
 * are ×100 (AGENTS `%`-presence rule — no magnitude heuristics).
 */

function asNumber(value) {
  if (value === null || value === undefined || value === '') return null
  const n = Number(String(value).replace(/,/g, '').trim())
  return Number.isNaN(n) ? null : n
}

/** Ratio or percent-string → percentage points for threshold compares. */
export function asPctPoints(value) {
  if (value === null || value === undefined || value === '') return null
  const s = String(value).trim()
  if (!s || s.toLowerCase() === 'nan' || s.toLowerCase() === 'none') return null
  if (s.includes('%')) {
    const n = Number(s.replace(/%/g, '').replace(/,/g, '').trim())
    return Number.isNaN(n) ? null : n
  }
  const n = Number(s.replace(/,/g, ''))
  if (Number.isNaN(n)) return null
  return n * 100
}

function meetsSimple(value, config, row) {
  if (value === null || value === undefined) return false
  const minVal = config.min
  const maxVal = config.max
  if (minVal !== undefined && value < minVal) return false
  if (maxVal !== undefined && value > maxVal) return false

  const pctOfCol = config.percentage_of_column
  const minPct = config.min_percentage
  if (pctOfCol) {
    const compare = asNumber(row?.[pctOfCol])
    if (compare === null || compare <= 0) return false
    const pct = (value / compare) * 100
    if (minPct !== undefined && pct < minPct) return false
  }

  const ranges = config.follower_count_ranges
  const followerCol = config.follower_count_column
  if (followerCol && ranges?.length) {
    const followers = asNumber(row?.[followerCol])
    if (followers === null) return false
    const band = ranges.find((r) => {
      const lo = r.min ?? 0
      const hi = r.max ?? Infinity
      return followers >= lo && followers < hi
    })
    if (!band) return false
    if (band.min_value !== undefined && value < band.min_value) return false
    if (band.max_value !== undefined && value >= band.max_value) return false
  }

  return true
}

const IG_NON_REELS_BANDS = {
  green: {
    follower_count_column: 'IG Follower Count',
    follower_count_ranges: [
      { min: 0, max: 10000, min_value: 20, max_value: 30 },
      { min: 10000, max: 100000, min_value: 12, max_value: 20 },
      { min: 100000, max: 500000, min_value: 7, max_value: 10 },
      { min: 500000, max: Infinity, min_value: 3, max_value: 5 },
    ],
  },
  yellow: {
    follower_count_column: 'IG Follower Count',
    follower_count_ranges: [
      { min: 0, max: 10000, min_value: 30 },
      { min: 10000, max: 100000, min_value: 20 },
      { min: 100000, max: 500000, min_value: 10 },
      { min: 500000, max: Infinity, min_value: 5 },
    ],
  },
}

/** @type {Record<string, { kind: 'rate' | 'count', green?: object, yellow?: object }>} */
const ENGAGEMENT_RULES = {
  '30D Average IG Reels Engagement Rate (max 12)': {
    kind: 'rate',
    green: { min: 7 },
    yellow: { min: 10 },
  },
  '30D Median IG Non-Reels Engagement Rate (max 12)': {
    kind: 'rate',
    ...IG_NON_REELS_BANDS,
  },
  '30D Median IG Reels Views (max 12)': {
    kind: 'count',
    green: {
      min: 20000,
      percentage_of_column: 'IG Follower Count',
      min_percentage: 40,
    },
    yellow: {
      min: 50000,
      percentage_of_column: 'IG Follower Count',
      min_percentage: 100,
    },
  },
  '30D Median TT Views (max 20)': {
    kind: 'count',
    green: {
      min: 20000,
      percentage_of_column: 'TT Follower Count',
      min_percentage: 40,
    },
    yellow: {
      min: 50000,
      percentage_of_column: 'TT Follower Count',
      min_percentage: 100,
    },
  },
  '30D Average TT Engagement Rate (max 20)': {
    kind: 'rate',
    green: { min: 12, max: 18 },
    yellow: { min: 20 },
  },
  '30D Average TT Saves Rate (max 20)': {
    kind: 'rate',
    green: { min: 2, max: 3 },
    yellow: { min: 3 },
  },
}

export const ENGAGEMENT_HIGHLIGHT_COLUMNS = Object.keys(ENGAGEMENT_RULES)

/**
 * @returns {'yellow' | 'green' | null}
 */
export function engagementHighlightTone(row, column) {
  const rule = ENGAGEMENT_RULES[column]
  if (!rule || !row) return null

  const raw = row[column]
  const value = rule.kind === 'rate' ? asPctPoints(raw) : asNumber(raw)
  if (value === null) return null

  if (rule.yellow && meetsSimple(value, rule.yellow, row)) return 'yellow'
  if (rule.green && meetsSimple(value, rule.green, row)) return 'green'
  return null
}

export const ENGAGEMENT_TONE_TITLE = {
  yellow: 'Email yellow engagement threshold',
  green: 'Email green engagement threshold',
}
